import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import Loading from 'src/components/loading';
import { PageLayout } from 'src/layouts';
import ActionModal from 'src/pages/actions/ActionModal';
import { ObligationChangeDetails } from 'src/pages/compliance/obligations/blocks/obligation-change-details';
import { toLocalDate } from 'src/utils/dateUtils';
import { getFriendlyId } from 'src/utils/friendlyId';

import { useGetObligationChangeById } from '@/hooks/queries/obligation-change/useGetObligationChangeById';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import {
  obligationChangesRegisterUrl,
  obligationDetailsUrl,
} from '@/utils/urls';

import { useCreateObligationChangeAction } from './useCreateObligationChangeAction';
import { useObligationChangeAttestation } from './useObligationChangeAttestation';

const ObligationChangeDetailPage: FC = () => {
  const { obligationChangeId } = useParams<{ obligationChangeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common', { keyPrefix: 'obligationChanges' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  const { data, loading, refetch } = useGetObligationChangeById({
    queryArgs: { obligationChangeId: obligationChangeId! },
  });

  const record = data?.obligation_change?.[0];

  const {
    isRead,
    markAsRead,
    markAsUnread,
    insertAttestationLoading,
    deleteAttestationLoading,
  } = useObligationChangeAttestation({
    obligationChangeId,
    attestations: record?.attestations,
    refetch,
  });

  const {
    isModalOpen,
    openModal,
    closeModal,
    createAction,
    insertActionLoading,
  } = useCreateObligationChangeAction({
    obligationChangeId,
    obligationId: record?.obligation?.Id,
    refetch,
  });

  const sequentialIdLabel = record
    ? `(${getFriendlyId('obligation_change', record.SequentialId)})`
    : '';

  const title = record?.obligation?.Title || t('detail_title');

  // Object-level toolbar (CUS-162) — flag-gated, additive. This detail page is
  // bespoke (a read-only summary plus Mark as read/unread + Create action
  // buttons in the body — no editable form, no tabs, no delete), so the toolbar
  // is title-only. The body action buttons are left in place as the flag-off /
  // flag-on behaviour, so nothing is removed.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title,
        // Pass the already-parenthesised counter (rendered verbatim).
        counter: sequentialIdLabel || undefined,
      },
    }),
    [title, sequentialIdLabel]
  );

  return (
    <PageLayout
      helpTranslationKey={'obligationChanges.registerHelp'}
      title={title}
      counter={sequentialIdLabel}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <SpaceBetween size={'m'}>
          <ObligationChangeDetails
            lang={{
              cards: {
                details: t('details'),
                current: t('current'),
                upcoming: t('upcoming'),
              },
              status: {
                unread: t('unread'),
                read: t('read'),
              },
              details: {
                status: t('columns.status'),
                effectiveDate: t('columns.effective_date'),
                regulatoryBody: t('columns.regulator'),
                referenceCode: t('columns.reference'),
              },
            }}
            state={{
              currentDescription:
                data?.obligation_change?.[0]?.DescriptionBefore ??
                'No description provided',
              upcomingDescription:
                data?.obligation_change?.[0]?.DescriptionAfter ||
                'No description provided',
              effectiveDate: data?.obligation_change?.[0]?.EffectiveDate
                ? toLocalDate(data?.obligation_change?.[0]?.EffectiveDate)
                : '-',
              status: isRead ? 'read' : 'unread',
              regulatoryBody:
                data?.obligation_change?.[0]?.obligation?.regulatorySource
                  ?.RegulatorName || '-',
              referenceCode:
                data?.obligation_change?.[0]?.obligation?.Reference || '-',
              obligationUrl: record?.ObligationId
                ? obligationDetailsUrl(record.ObligationId)
                : undefined,
            }}
          />

          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              variant={'normal'}
              onClick={() => navigate(obligationChangesRegisterUrl())}
              disabled={
                insertActionLoading ||
                insertAttestationLoading ||
                deleteAttestationLoading
              }
            >
              {t('cancel')}
            </Button>
            {!isRead && (
              <Button
                variant={'normal'}
                onClick={markAsRead}
                loading={insertActionLoading || insertAttestationLoading}
              >
                {t('mark_as_read')}
              </Button>
            )}
            {isRead && (
              <Button
                variant={'normal'}
                onClick={markAsUnread}
                loading={insertActionLoading || deleteAttestationLoading}
              >
                {t('mark_as_unread')}
              </Button>
            )}
            <Button
              variant={'primary'}
              onClick={openModal}
              loading={
                insertActionLoading ||
                insertAttestationLoading ||
                deleteAttestationLoading
              }
            >
              {t('create_action')}
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      )}

      {isModalOpen && (
        <ActionModal onSaving={createAction} onDismiss={closeModal} />
      )}
    </PageLayout>
  );
};

export default ObligationChangeDetailPage;
